'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Color } from '@diritto/common/style/color';
import { LocalStorageKey } from '@diritto/common/utils/localStorage';
import {
  ViewerFont,
  ViewerFonts,
  ViewerFontSize,
  ViewerLineHeight,
  ViewerOptionKey,
  ViewerPadding,
  ViewerParagraphIndent,
  ViewerStyle,
  ViewerTheme,
  ViewerViewType,
} from '@diritto/common/viewer/constants/viewer.constant';
import { initialStyle, ViewerOptionKeyToLocalStorageKey, ViewerThemeColorSet } from 'constants/viewer';
import { useTrackingState } from 'contexts/TrackingProvider';
import { LocalStorage } from 'utils/LocalStorage';
import { showToast } from 'utils/modal';

interface ViewerStyleState {
  viewerOptions: ViewerStyle;
  dividerColor: string;
  color: Color;
  logoColor: Color;
  backgroundColor: Color;
}

interface ViewerStyleDispatch {
  changeOption: (key: ViewerOptionKey, value: ViewerStyle[ViewerOptionKey]) => void;
}

const ViewerStyleStateContext = createContext<ViewerStyleState>({} as ViewerStyleState);

const ViewerStyleDispatchContext = createContext<ViewerStyleDispatch>({} as ViewerStyleDispatch);

export const ViewerStyleProvider = ({ children }: { children: ReactNode }) => {
  const [viewerOptions, setOptions] = useState<ViewerStyle>(initialStyle);
  const { tracker } = useTrackingState();

  const isBrightTheme = useCallback((theme: ViewerTheme) => {
    const BrightThemeList = [ViewerTheme.White, ViewerTheme.Sepia, ViewerTheme.DirittoGreen];
    return BrightThemeList.indexOf(theme) > -1;
  }, []);

  const state = useMemo(
    () => ({
      viewerOptions,
      dividerColor: isBrightTheme(viewerOptions[ViewerOptionKey.Theme]) ? Color.Black : Color.White,
      color: ViewerThemeColorSet[viewerOptions[ViewerOptionKey.Theme]].color,
      logoColor: ViewerThemeColorSet[viewerOptions[ViewerOptionKey.Theme]].logoColor,
      backgroundColor: ViewerThemeColorSet[viewerOptions[ViewerOptionKey.Theme]].backgroundColor,
    }),
    [isBrightTheme, viewerOptions],
  );

  useEffect(() => {
    const theme = LocalStorage.getItem(LocalStorageKey.ViewerTheme) as ViewerTheme;
    const font = LocalStorage.getItem(LocalStorageKey.ViewerFont) as ViewerFont;
    const fontSize = LocalStorage.getItem(LocalStorageKey.ViewerFontSize) as ViewerFontSize;
    const lineHeight = LocalStorage.getItem(LocalStorageKey.ViewerLineHeight) as ViewerLineHeight;
    const paragraphIndent = LocalStorage.getItem(LocalStorageKey.ViewerParagraphIndent) as ViewerParagraphIndent;
    const padding = LocalStorage.getItem(LocalStorageKey.ViewerPadding) as ViewerPadding;
    const indentation = LocalStorage.getItem(LocalStorageKey.ViewerIndentation) === 'true';
    const viewType = LocalStorage.getItem(LocalStorageKey.ViewerViewType) as ViewerViewType;
    const fancyShare = LocalStorage.getItem(LocalStorageKey.ViewerFancyShare);

    setOptions({
      [ViewerOptionKey.Theme]: Object.values(ViewerTheme).includes(theme) ? theme : initialStyle[ViewerOptionKey.Theme],
      [ViewerOptionKey.Font]: Object.values(ViewerFonts).includes(font) ? font : initialStyle[ViewerOptionKey.Font],
      [ViewerOptionKey.FontSize]: Object.values(ViewerFontSize).includes(fontSize)
        ? fontSize
        : initialStyle[ViewerOptionKey.FontSize],
      [ViewerOptionKey.LineHeight]: Object.values(ViewerLineHeight).includes(lineHeight)
        ? lineHeight
        : initialStyle[ViewerOptionKey.LineHeight],
      [ViewerOptionKey.ParagraphIndent]: Object.values(ViewerParagraphIndent).includes(paragraphIndent)
        ? paragraphIndent
        : initialStyle[ViewerOptionKey.ParagraphIndent],
      [ViewerOptionKey.Padding]: Object.values(ViewerPadding).includes(padding)
        ? padding
        : initialStyle[ViewerOptionKey.Padding],
      [ViewerOptionKey.Indentation]: indentation || initialStyle[ViewerOptionKey.Indentation],
      [ViewerOptionKey.ViewType]: viewType || initialStyle[ViewerOptionKey.ViewType],
      [ViewerOptionKey.FancyShare]: fancyShare ? fancyShare === 'true' : initialStyle[ViewerOptionKey.FancyShare],
    });
  }, []);

  useEffect(() => {
    if (!document) {
      return () => {};
    }
    const style = document.querySelector('body')?.style;
    style!.backgroundColor = `${state.backgroundColor}`;
    document.querySelector('meta[name="msapplication-TileColor"]')?.setAttribute('content', state.backgroundColor);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', state.backgroundColor);
    document
      .querySelector('meta[name="msapplication-navbutton-color"]')
      ?.setAttribute('content', state.backgroundColor);

    return () => {
      if (!document) {
        return;
      }
      style!.backgroundColor = `${Color.White}`;
      document.querySelector('meta[name="msapplication-TileColor"]')?.setAttribute('content', Color.White);
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', Color.White);
      document.querySelector('meta[name="msapplication-navbutton-color"]')?.setAttribute('content', Color.White);
    };
  }, [state.backgroundColor]);

  const changeOption = useCallback<ViewerStyleDispatch['changeOption']>(
    (key, value) => {
      setOptions(prev => ({ ...prev, [key]: value }));
      LocalStorage.setItem(ViewerOptionKeyToLocalStorageKey[key], String(value));
      tracker.trackChangeViewerSetting({ [ViewerOptionKeyToLocalStorageKey[key]]: value });

      if (key === ViewerOptionKey.ViewType) {
        showToast(`${value === ViewerViewType.Page ? '페이지' : '스크롤'}로 보기로 전환되었어요.`);
        LocalStorage.setItem(LocalStorageKey.ViewerViewTypeNoti, value as ViewerViewType);
      }

      if (key === ViewerOptionKey.FancyShare) {
        showToast(`이멋공 기능이 ${value ? '켜' : '꺼'}졌어요.`);
      }
    },
    [tracker],
  );

  useEffect(() => {
    const value = LocalStorage.getItem(LocalStorageKey.ViewerViewTypeNoti);
    LocalStorage.removeItem(LocalStorageKey.ViewerViewTypeNoti);

    if (value) {
      showToast(`${value === ViewerViewType.Page ? '페이지' : '스크롤'}로 보기`);
    }
  }, []);

  useEffect(() => {
    tracker.trackViewerSetting(viewerOptions);
  }, [tracker, viewerOptions]);

  const dispatch = useMemo(() => ({ changeOption }), [changeOption]);

  return (
    <ViewerStyleDispatchContext.Provider value={dispatch}>
      <ViewerStyleStateContext.Provider value={state}>{children}</ViewerStyleStateContext.Provider>
    </ViewerStyleDispatchContext.Provider>
  );
};

export const useViewerStyleState = () => {
  const state = useContext(ViewerStyleStateContext);
  if (!state) {
    throw new Error('Cannot find ViewerStyleStateContext');
  }
  return state;
};

export const useViewerStyleDispatch = () => {
  const dispatch = useContext(ViewerStyleDispatchContext);
  if (!dispatch) {
    throw new Error('Cannot find ViewerStyleDispatchContext');
  }
  return dispatch;
};
